/**
 * Game Room Screen - Waiting room before game starts (v2 API)
 *
 * Generic component supporting 2-12 players. Uses room metadata only.
 */

import { useEffect, useState } from "react";
import { getGameType } from "../../game-registry";
import {
  buildGamePath,
  buildLobbyPath,
  navigateTo,
  type RouteMode,
} from "../../router";
import { Alert, Badge, Button, Card, Input, Toast } from "../../ui";
import { orderBotsForDisplay } from "../config/ai-bots";
import { getConfig } from "../config/runtime-config";
import { useGameRoomWS } from "../hooks/use-game-room-ws";
import { useWsReconnectTimeout } from "../hooks/use-ws-reconnect-timeout";
import { useAuth } from "../store/auth-store";
import { useLobby, useLobbyActions } from "../store/lobby-store";
import { getParticipantDisplayName } from "../utils/participants";
import { deriveRoomDisplayName } from "../utils/room-display";

interface GameRoomScreenProps {
  readonly gameType: string;
  readonly routeMode?: RouteMode;
}

export function GameRoomScreen({
  gameType,
  routeMode = "v2",
}: GameRoomScreenProps) {
  const { user } = useAuth();
  const { currentRoom, isLoading, error } = useLobby();
  const lobbyActions = useLobbyActions();
  const gameConfig = getGameType(gameType);

  const isHost = currentRoom?.hostUserId === user?.id;
  const playerCount = currentRoom?.currentPlayerCount ?? 1;
  const minPlayers = gameConfig?.minPlayers ?? 2;
  const canStart = isHost && playerCount >= minPlayers;
  const roomId = currentRoom?.id ?? null;

  const { connectionStatus } = useGameRoomWS(
    roomId,
    gameType,
    lobbyActions,
    routeMode,
  );
  const reconnectTimedOut = useWsReconnectTimeout(connectionStatus);
  const [shareLinkUrl, setShareLinkUrl] = useState<string | null>(null);
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [isGeneratingObserverLink, setIsGeneratingObserverLink] =
    useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<"easy" | "normal" | "hard">(
    "normal",
  );
  const [isInvitingAi, setIsInvitingAi] = useState(false);
  const [aiInviteError, setAiInviteError] = useState<string | null>(null);

  // AI opponent invite: registry opt-in per game + bot worker URL configured.
  const config = getConfig();
  const aiBotServiceUrl = config.aiBotServiceUrl;
  const supportsAiOpponent =
    gameConfig?.supportsAiOpponent === true && aiBotServiceUrl !== "";

  // Per-bot AI invite (YINSH and future games with a config-driven roster).
  // The roster comes entirely from runtime config — numbered newest-first so the
  // latest champion sits at the top (e.g. "5. ai-fp-gen067").
  const displayBots = orderBotsForDisplay(config.aiBots[gameType] ?? []);
  const supportsPerBotAi =
    gameConfig?.supportsAiOpponent === true && displayBots.length > 0;
  const [selectedBotId, setSelectedBotId] = useState<string>(
    displayBots[0]?.id ?? "",
  );

  const handleInviteAi = async () => {
    if (!roomId) return;
    setIsInvitingAi(true);
    setAiInviteError(null);
    try {
      const response = await fetch(`${aiBotServiceUrl}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, difficulty: aiDifficulty }),
      });
      const body = (await response.json()) as {
        success: boolean;
        error?: string;
      };
      if (!body.success) {
        setAiInviteError(body.error ?? "AI 초대에 실패했습니다");
      } else {
        setToastMessage("AI 플레이어가 입장했습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      }
    } catch {
      setAiInviteError("AI 서버에 연결할 수 없습니다");
    }
    setIsInvitingAi(false);
  };

  const handleInvitePerBot = async () => {
    if (!roomId) return;
    const bot = displayBots.find((b) => b.id === selectedBotId);
    if (!bot) return;
    setIsInvitingAi(true);
    setAiInviteError(null);
    try {
      const response = await fetch(`${bot.url}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId }),
      });
      const body = (await response.json()) as {
        success: boolean;
        error?: string;
      };
      if (!body.success) {
        setAiInviteError(body.error ?? "AI 초대에 실패했습니다");
      } else {
        setToastMessage("AI 플레이어가 입장했습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      }
    } catch {
      setAiInviteError("AI 서버에 연결할 수 없습니다");
    }
    setIsInvitingAi(false);
  };

  const handleStartGame = async () => {
    await lobbyActions.startGame();
  };

  const handleGenerateShareLink = async () => {
    setIsGeneratingShareLink(true);
    const shareLink = await lobbyActions.createShareLink();
    setIsGeneratingShareLink(false);

    if (shareLink) {
      setShareLinkUrl(shareLink.url);
      try {
        await navigator.clipboard.writeText(shareLink.url);
        setToastMessage("공유 링크가 복사되었습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      } catch {
        setToastMessage("링크가 생성되었습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      }
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareLinkUrl) return;

    try {
      await navigator.clipboard.writeText(shareLinkUrl);
      setToastMessage("링크가 클립보드에 복사되었습니다");
      setTimeout(() => {
        setToastMessage(null);
      }, 2000);
    } catch {
      setToastMessage("링크 복사에 실패했습니다");
      setTimeout(() => {
        setToastMessage(null);
      }, 2000);
    }
  };

  const handleGenerateObserverLink = async () => {
    setIsGeneratingObserverLink(true);
    const shareLink = await lobbyActions.createObserverShareLink();
    setIsGeneratingObserverLink(false);

    if (shareLink) {
      setShareLinkUrl(shareLink.url);
      try {
        await navigator.clipboard.writeText(shareLink.url);
        setToastMessage("관전자 링크가 복사되었습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      } catch {
        setToastMessage("관전자 링크가 생성되었습니다");
        setTimeout(() => {
          setToastMessage(null);
        }, 2000);
      }
    }
  };

  const handleLeaveGame = async () => {
    if (isHost) {
      await lobbyActions.cancelGame();
    } else {
      await lobbyActions.leaveGame();
    }
    navigateTo(buildLobbyPath(gameType, routeMode));
  };

  useEffect(() => {
    if (!currentRoom) return;
    if (
      currentRoom.status === "IN_PROGRESS" ||
      currentRoom.status === "COMPLETED"
    ) {
      navigateTo(buildGamePath(gameType, currentRoom.id, routeMode));
    }
  }, [currentRoom, gameType, routeMode]);

  if (!currentRoom) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        불러오는 중...
      </div>
    );
  }

  const displayName = gameConfig?.displayName ?? gameType;
  const maxPlayers = gameConfig?.maxPlayers ?? 2;
  const isFull = playerCount >= maxPlayers;
  const supportsObservers = gameConfig?.supportsObservers === true;
  const observerMode = supportsObservers && currentRoom.observerMode;
  const observerUserIds = currentRoom.observerUserIds ?? [];

  const connectionBadgeColor: "green" | "yellow" =
    connectionStatus === "connected" ? "green" : "yellow";

  const connectionLabel =
    connectionStatus === "connected" ? "연결됨" : "재연결 중...";

  return (
    <div
      data-testid="game-room"
      className="flex min-h-screen flex-col items-center justify-center p-3 sm:p-6"
    >
      <Card className="w-full shadow-lg sm:max-w-md sm:p-8">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-lg">게임 대기실</h1>
            <p className="mt-1 text-text-muted text-xs">
              {deriveRoomDisplayName(currentRoom.id)} · {displayName}
            </p>
          </div>
          <Badge color={connectionBadgeColor}>{connectionLabel}</Badge>
        </div>

        {error && <Alert className="mb-4">{error}</Alert>}

        {/* Player List */}
        <div className="mb-4">
          <div className="mb-2 font-semibold text-sm text-text-secondary">
            플레이어 ({playerCount}/{maxPlayers})
          </div>
          <div className="flex flex-col gap-1.5">
            {(currentRoom.participantUserIds ?? []).map((userId) => {
              const isCurrentUser = userId === user?.id;
              const isHostUser = userId === currentRoom.hostUserId;
              const displayName = getParticipantDisplayName(
                currentRoom,
                userId,
                "플레이어",
              );
              const isAiPlayer = displayName.toLowerCase().startsWith("ai-");
              return (
                <div
                  key={userId}
                  data-testid="participant-row"
                  className="flex items-center gap-3 rounded-md border border-border-subtle bg-surface-secondary px-3 py-2.5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-blue-bg text-sm">
                    {isHostUser ? "👑" : isAiPlayer ? "🤖" : "👤"}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">
                      {displayName}
                      {isCurrentUser ? " (나)" : ""}
                      {isHostUser ? " (호스트)" : ""}
                    </div>
                    <div
                      className="text-text-muted text-xs"
                      data-testid={
                        isAiPlayer ? "ai-participant-label" : undefined
                      }
                    >
                      {isHostUser
                        ? "호스트"
                        : isAiPlayer
                          ? "AI 플레이어"
                          : "참가자"}
                    </div>
                  </div>
                </div>
              );
            })}
            {Array.from({ length: maxPlayers - playerCount }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="flex items-center gap-3 rounded-md border border-border-subtle border-dashed px-3 py-2.5"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-tertiary text-sm text-text-hint">
                  ?
                </div>
                <div>
                  <div className="text-sm text-text-hint">대기 중...</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {observerMode && (
          <div className="mb-4">
            <div className="mb-2 font-semibold text-sm text-text-secondary">
              관전자 ({currentRoom.currentObserverCount}/
              {currentRoom.observerSeatCount})
            </div>
            <div
              data-testid="observer-section"
              className="rounded-md border border-border-subtle bg-surface-secondary px-3 py-2.5 text-sm text-text-secondary"
            >
              {observerUserIds.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {observerUserIds.map((observerId, index) => (
                    <div
                      key={observerId}
                      data-testid="observer-row"
                      className="flex items-center justify-between rounded-md border border-border-subtle bg-surface-primary px-3 py-2"
                    >
                      <span className="font-semibold text-text-primary">
                        {currentRoom.observerParticipants
                          ?.find((observer) => observer.userId === observerId)
                          ?.displayName.trim() ||
                          getParticipantDisplayName(
                            currentRoom,
                            observerId,
                            `관전자 ${index + 1}`,
                          )}
                        {observerId === user?.id ? " (나)" : ""}
                      </span>
                      <span className="text-text-muted text-xs">읽기 전용</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span>아직 입장한 관전자가 없습니다.</span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {isHost && !isFull && supportsPerBotAi && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2">
                <select
                  data-testid="ai-bot-select"
                  value={selectedBotId}
                  onChange={(event) => setSelectedBotId(event.target.value)}
                  disabled={isInvitingAi}
                  className="rounded-md border border-border-subtle bg-surface-primary px-2 py-2 text-sm"
                >
                  {displayBots.map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot.displayLabel}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  data-testid="invite-ai-btn"
                  onClick={handleInvitePerBot}
                  disabled={isInvitingAi || isLoading}
                  className="flex-1"
                >
                  {isInvitingAi ? "AI 초대 중..." : "🤖 AI 상대 초대"}
                </Button>
              </div>
              {aiInviteError && (
                <p
                  data-testid="ai-invite-error"
                  className="text-accent-red text-xs"
                >
                  {aiInviteError}
                </p>
              )}
            </div>
          )}

          {isHost && !isFull && supportsAiOpponent && !supportsPerBotAi && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2">
                <select
                  data-testid="ai-difficulty-select"
                  value={aiDifficulty}
                  onChange={(event) =>
                    setAiDifficulty(
                      event.target.value as "easy" | "normal" | "hard",
                    )
                  }
                  disabled={isInvitingAi}
                  className="rounded-md border border-border-subtle bg-surface-primary px-2 py-2 text-sm"
                >
                  <option value="easy">야수의심장</option>
                  <option value="normal">중꺽마</option>
                  <option value="hard">관심법</option>
                </select>
                <Button
                  variant="secondary"
                  data-testid="invite-ai-btn"
                  onClick={handleInviteAi}
                  disabled={isInvitingAi || isLoading}
                  className="flex-1"
                >
                  {isInvitingAi ? "AI 초대 중..." : "🤖 AI 상대 초대"}
                </Button>
              </div>
              {aiInviteError && (
                <p
                  data-testid="ai-invite-error"
                  className="text-accent-red text-xs"
                >
                  {aiInviteError}
                </p>
              )}
            </div>
          )}

          {isHost && !isFull && (
            <Button
              variant="secondary"
              data-testid="generate-share-link-btn"
              onClick={handleGenerateShareLink}
              disabled={isGeneratingShareLink || isLoading}
              className="w-full"
            >
              {isGeneratingShareLink ? "링크 생성 중..." : "🔗 공유 링크 생성"}
            </Button>
          )}

          {isHost && observerMode && (
            <Button
              variant="secondary"
              data-testid="generate-observer-link-btn"
              onClick={handleGenerateObserverLink}
              disabled={isGeneratingObserverLink || isLoading}
              className="w-full"
            >
              {isGeneratingObserverLink
                ? "관전자 링크 생성 중..."
                : "◎ 관전자 링크 생성"}
            </Button>
          )}

          {shareLinkUrl && (
            <div className="flex gap-2">
              <Input
                type="text"
                readOnly
                value={shareLinkUrl}
                data-testid="share-link-url"
                className="flex-1 bg-surface-primary text-xs"
              />
              <Button
                variant="secondary"
                size="sm"
                data-testid="share-link-copy-btn"
                onClick={handleCopyShareLink}
              >
                복사
              </Button>
            </div>
          )}

          {isHost && (
            <Button
              variant="success"
              size="md"
              data-testid="start-game-btn"
              onClick={handleStartGame}
              disabled={!canStart || isLoading}
              className="w-full"
            >
              {!canStart
                ? `게임 시작 (인원 부족)`
                : isLoading
                  ? "시작 중..."
                  : "게임 시작"}
            </Button>
          )}
        </div>

        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            data-testid="leave-game-btn"
            onClick={handleLeaveGame}
            disabled={isLoading}
          >
            방 나가기
          </Button>
        </div>
      </Card>

      <Toast show={toastMessage !== null} message={toastMessage ?? ""} />

      {reconnectTimedOut && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-xl bg-surface-primary p-8 text-center shadow-xl">
            <p className="mb-2 font-semibold text-text-primary">
              연결이 끊어졌습니다
            </p>
            <p className="mb-6 text-sm text-text-secondary">
              서버와의 연결이 복구되지 않았습니다.
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => window.location.reload()}
              >
                새로고침
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigateTo(buildLobbyPath(gameType, routeMode))}
              >
                로비로 돌아가기
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
